import React, { forwardRef, useCallback, useMemo, useState } from 'react'
import { useForm } from '@mantine/form'
import { passwordRules } from 'src/variable/constant'
import { Flex, Stack, TextInput, Grid, Select, Button, Group, Textarea, Checkbox, Radio } from '@mantine/core'
import CustomPasswordInput from 'src/components/CustomComponents/CustomInputPassword/CustomPasswordInput'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import md5 from 'md5'
import { supportCreateAccount } from 'src/apis/userAPI'
import { useModal } from 'src/context/ModalContext'
import CustomModal from 'src/components/CustomComponents/CustomModal'
// Import danh sách quốc gia từ thư viện
import { countries } from 'countries-list'

// Flag pictures for the dialling-code list. Emoji flags are not drawn on
// Windows, where they show as two letters ("VN", "TD"), so a small image is used.
const flagUrl = (code: string) => `https://flagcdn.com/w20/${code.toLowerCase()}.png`

const Flag = ({ code }: { code?: string }) =>
	code ? (
		<img src={flagUrl(code)} width={20} height={15} alt="" loading="lazy" style={{ borderRadius: 2, flexShrink: 0 }} />
	) : null

// Dialling codes shared by several countries, and the one the list shows for each
const MAIN_COUNTRY_FOR_CODE: Record<string, string> = {
	'+1': 'US',
	'+7': 'RU',
	'+44': 'GB',
	'+47': 'NO',
	'+61': 'AU',
	'+64': 'NZ',
	'+212': 'MA',
	'+262': 'RE',
	'+290': 'SH',
	'+358': 'FI',
	'+377': 'MC',
	'+500': 'FK',
	'+590': 'GP',
	'+672': 'NF',
}

type CountryItemProps = React.ComponentPropsWithoutRef<'div'> & { label: string; code: string }

const CountryItem = forwardRef<HTMLDivElement, CountryItemProps>(({ label, code, ...others }, ref) => (
	<div ref={ref} {...others}>
		<Group noWrap spacing={8}>
			<Flag code={code} />
			<span>{label}</span>
		</Group>
	</div>
))
CountryItem.displayName = 'CountryItem'

// Channels staff may use to confirm that the phone number belongs to the person
const OWNER_VERIFIED_CHANNELS = [
	{ value: 'whatsapp', label: 'WhatsApp' },
	{ value: 'zalo', label: 'Zalo' },
]

interface ModalCreateNormalUserProps {
	open: boolean
	onClose: () => void
	handleGetData: () => void
	pagination: {
		pageIndex: number
		pageSize: number
	}
	handleChangePage: (_value: number) => void
	[key: string]: any
}

function ModalCreateNormalUser({
	open,
	onClose,
	handleGetData,
	pagination,
	handleChangePage,
}: ModalCreateNormalUserProps) {
	const [copied, setCopied] = useState(false)
	const [copiedEmail, setCopiedEmail] = useState(false) // State cho nút copy email
	const countryOptions = useMemo(() => {
		const seen = new Set<string>()

		return Object.entries(countries).reduce<Array<{ value: string; label: string; code: string }>>(
			(acc, [code, country]) => {
				const primaryPhone = country.phone?.[0] || ''
				const dialCode = `+${String(primaryPhone).replace(/[^\d]/g, '')}`

				// Skip invalid or duplicate prefixes (e.g. +262 appears in multiple territories)
				if (!dialCode || dialCode === '+') {
					return acc
				}
				const option = { value: dialCode, label: `${country.name} (${dialCode})`, code }
				if (seen.has(dialCode)) {
					// a shared code shows its main country: +1 is the United States, not Canada
					if (MAIN_COUNTRY_FOR_CODE[dialCode] === code) {
						const index = acc.findIndex((item) => item.value === dialCode)
						if (index >= 0) acc[index] = option
					}
					return acc
				}
				seen.add(dialCode)

				acc.push(option)
				return acc
			},
			[],
		)
	}, [])



	const form = useForm({
		initialValues: {
			name: '',
			email: '',
			prefix_phone: '+84',
			phone: '',
			password: '',
			owner_verified: false,
			owner_verified_channel: '',
		},

		validate: {
			name: (value) => {
				if (!value || !value.trim()) return null
				if (value.trim().length < 2 || value.length > 50)
					return 'Name must be 2-50 characters long'
				return null
			},
			email: (value) => {
				if (value) {
					const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
					if (regex.test(value)) return null
					else return 'Invalid Email'
				}
				return null
			},
			password: (value) => {
				if (!value.trim()) return 'Password cannot be empty'
				if (!passwordRules.test(value)) {
					return 'Password must be 8-20 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character'
				}
			},
			phone: (value) => {
				const phoneRegex = /^\d+$/
				if (!value) return 'Phone cannot be empty'
				if (!phoneRegex.test(value)) return 'Invalid phone'
				if (value.length < 9 || value.length > 15)
					return 'Phone must be 9-15 characters long'
				return null
			},
			owner_verified: (value) =>
				value ? null : 'Confirm with the owner of the phone number first',
			owner_verified_channel: (value) =>
				OWNER_VERIFIED_CHANNELS.some((item) => item.value === value)
					? null
					: 'Choose WhatsApp or Zalo',
		},
		validateInputOnBlur: true,
		validateInputOnChange: true,
	})

	const { openModal, closeModal, openError } = useModal()
	const resetForm = form.reset
	// Create stays off until the box is ticked and a channel is chosen
	const ownerConfirmed = form.values.owner_verified && !!form.values.owner_verified_channel

	// Hàm tạo mật khẩu ngẫu nhiên chuẩn format (độ dài 14 ký tự)
	const handleAutoGeneratePassword = () => {
		const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
		const lowercase = 'abcdefghijklmnopqrstuvwxyz'
		const numbers = '0123456789'
		const specials = '@'
		const allChars = uppercase + lowercase + numbers + specials

		let generatedPassword = ''
		// Bắt buộc phải có ít nhất 1 ký tự từ mỗi nhóm ký tự
		generatedPassword += uppercase[Math.floor(Math.random() * uppercase.length)]
		generatedPassword += lowercase[Math.floor(Math.random() * lowercase.length)]
		generatedPassword += numbers[Math.floor(Math.random() * numbers.length)]
		generatedPassword += specials[Math.floor(Math.random() * specials.length)]

		// Thêm 10 ký tự ngẫu nhiên bất kỳ để đạt độ dài 14 ký tự (nằm trong khoảng từ 8-20)
		for (let i = 0; i < 10; i++) {
			generatedPassword += allChars[Math.floor(Math.random() * allChars.length)]
		}

		// Trộn ngẫu nhiên chuỗi ký tự vừa tạo để tăng tính bảo mật
		const finalPassword = generatedPassword.split('').sort(() => 0.5 - Math.random()).join('')

		form.setFieldValue('password', finalPassword)
	}

	// Hàm copy mật khẩu vào Clipboard
	const handleCopyPassword = () => {
		if (form.values.password) {
			navigator.clipboard.writeText(form.values.password)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000) // Trở lại chữ "Copy" sau 2 giây
		}
	}
	// Lấy mã vùng hiện tại dựa theo prefix đang chọn
	const emailContent = useMemo(() => {
		// const selectedCountry = countries[form.values.prefix_phone as keyof typeof countries]
		// const rawDialCode = selectedCountry && selectedCountry.phone.length > 0 ? selectedCountry.phone[0] : '84'
		const displayPhone = (form.values.phone && form.values.prefix_phone) ? `${form.values.prefix_phone}${form.values.phone}` : '[Phone Number]'
		const passwordVal = form.values.password || '[Password]'
		return `Hello,
Thank you for contacting UniVini Support.
We have successfully created your UniVini account.

Phone Number: ${displayPhone}
Temporary Password: ${passwordVal}

You can log in using the phone number above and change your password anytime by following:
Account → Change Password

If you experience any issues logging in, please reply to this email and our team will be happy to assist you.

Best regards,
UniVini Support`
	}, [form.values.phone, form.values.password, form.values.prefix_phone])
	// Hàm copy toàn bộ nội dung Email Template
	const handleCopyEmailContent = () => {
		navigator.clipboard.writeText(emailContent)
		setCopiedEmail(true)
		setTimeout(() => setCopiedEmail(false), 2000)
	}
	const handleCreateNormalUser = useCallback(async (body) => {
		try {
			const res = await supportCreateAccount(body)
			if (res?.data?.code === 200) {
				if (pagination.pageIndex === 1) {
					await handleGetData()
				} else {
					await handleChangePage(1)
				}
				// the next account needs its own owner confirmation
				resetForm()
				// the success message is a toast and never calls a close handler, so
				// the form is closed here
				closeModal()
				onClose()
				openModal({
					type: 'success',
					label: 'User has been created successfully',
				})
			}
		} catch (error) {
			console.log('Handle Submit ~ error:', error)
			openError(error)
		}
	}, [pagination.pageIndex, handleGetData, handleChangePage, openModal, closeModal, onClose, openError, resetForm])

	const handleSubmit = useCallback(async (values) => {
		const formattedPhone = String(values?.phone)?.startsWith('0')
			? values?.phone.slice(1)
			: values?.phone

		const body = {
			...values,
			password: md5(values.password),
			phone: values.prefix_phone + formattedPhone,
			prefix_phone: values.prefix_phone,
			owner_verified: values.owner_verified === true,
			owner_verified_channel: values.owner_verified_channel,
		}

		openModal({
			type: 'confirm',
			label: 'Do you want to create user ?',
			handleClose: () => closeModal(),
			handleConfirm: () => {
				handleCreateNormalUser(body)
			},
		})
	}, [openModal, closeModal, handleCreateNormalUser])

	return (
		<CustomModal
			size={'xl'}
			opened={open}
			onClose={onClose}
		>
			<form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
				<Stack spacing="1.25rem">
					<Flex sx={{ fontSize: 25, fontWeight: 700 }} justify="center">
						Create New User
					</Flex>

					<Grid gutter="md">
						{/* Dòng 1: Name (Không bắt buộc) & Email */}
						<Grid.Col span={6}>
							<TextInput
								autoComplete="off"
								label={'Name'}
								placeholder={"User's name"}
								maxLength={50}
								{...form.getInputProps('name')}
							/>
						</Grid.Col>
						<Grid.Col span={6}>
							<TextInput
								autoComplete="off"
								label={'Email'}
								placeholder={"User's email"}
								maxLength={50}
								{...form.getInputProps('email')}
							/>
						</Grid.Col>

						{/* Dòng 2: prefix_phone (Có Search) & Phone number */}
						<Grid.Col span={5}>
							<Select
								label="Prefix"
								withAsterisk
								searchable
								nothingFound="No countries found"
								maxDropdownHeight={300}
								data={countryOptions} // Truyền dữ liệu full nước vào đây
								itemComponent={CountryItem}
								icon={<Flag code={countryOptions.find((item) => item.value === form.values.prefix_phone)?.code} />}
								// onChange={(value) => {
								// 	// console.log(value);
								// 	// form.setFieldValue('prefix_phone', value || '+84')

								// }}
								{...form.getInputProps('prefix_phone')}

							/>
						</Grid.Col>
						<Grid.Col span={7}>
							<TextInput
								autoComplete="new-password"
								withAsterisk
								label={'Phone'}
								placeholder={"User's Phone"}
								maxLength={15}
								{...form.getInputProps('phone')}
							/>
						</Grid.Col>

						{/* Dòng 3: Password kèm cụm nút chức năng bên phải */}
						<Grid.Col span={12}>
							<Stack spacing={4}>
								<Group position="apart" noWrap>
									<label style={{ fontSize: '14px', fontWeight: 500 }}>
										Password <span style={{ color: 'red' }}>*</span>
									</label>
									<Group spacing="xs">
										<Button
											variant="subtle"
											size="xs"
											compact
											onClick={handleAutoGeneratePassword}
											styles={{ root: { height: 'auto', padding: 0 } }}
										>
											Auto Generate
										</Button>
										<Button
											variant="subtle"
											size="xs"
											compact
											color={copied ? 'green' : 'blue'}
											onClick={handleCopyPassword}
											disabled={!form.values.password}
											styles={{ root: { height: 'auto', padding: 0 } }}
										>
											{copied ? 'Copied!' : 'Copy'}
										</Button>
									</Group>
								</Group>
								<CustomPasswordInput
									autoComplete="new-password"
									placeholder={"User's password"}
									maxLength={50}
									{...form.getInputProps('password')}
								/>
							</Stack>
						</Grid.Col>
						<Grid.Col span={12}>
							<Stack spacing={4}>
								<Group position="apart" noWrap>
									<label style={{ fontSize: '14px', fontWeight: 500, color: '#666' }}>
										Email Notification Template
									</label>
									<Button
										variant="light"
										size="xs"
										color={copiedEmail ? 'green' : 'blue'}
										onClick={handleCopyEmailContent}
									>
										{copiedEmail ? 'Copied Content!' : 'Copy Email Content'}
									</Button>
								</Group>
								<Textarea
									readOnly
									autosize
									minRows={6}
									maxRows={10}
									value={emailContent}
									styles={{
										input: {
											backgroundColor: '#f8f9fa',
											fontFamily: 'monospace',
											fontSize: '13px',
											lineHeight: '1.5'
										}
									}}
								/>
							</Stack>
						</Grid.Col>

						{/* Owner confirmation: both are required before Create */}
						<Grid.Col span={12}>
							<Stack spacing={8}>
								<Checkbox
									label={
										<>
											Owner confirmed (verified by WhatsApp or Zalo) <span style={{ color: 'red' }}>*</span>
										</>
									}
									{...form.getInputProps('owner_verified', { type: 'checkbox' })}
								/>
								<Radio.Group
									label="Confirmation channel"
									withAsterisk
									{...form.getInputProps('owner_verified_channel')}
								>
									<Group spacing="lg" mt={4}>
										{OWNER_VERIFIED_CHANNELS.map((item) => (
											<Radio key={item.value} value={item.value} label={item.label} />
										))}
									</Group>
								</Radio.Group>
							</Stack>
						</Grid.Col>
					</Grid>

					<Flex justify="flex-end" style={{ marginTop: '1rem' }}>
						{/* CustomButton turns itself off while loading, so disabled is only passed to hold Create back */}
						<CustomButton
							variant="outlined"
							type="submit"
							{...(ownerConfirmed ? {} : { disabled: true })}
						>
							Create
						</CustomButton>
					</Flex>
				</Stack>
			</form>
		</CustomModal>
	)
}

export default React.memo(ModalCreateNormalUser)
